import type { IntakeAdapterHealth, OnboardingBriefing } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Play, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { AdminShell } from '@/features/admin/AdminShell';
import { useHealth, useStats, useUsers } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { sweepSentence } from '@/lib/fleet';
import { duration } from '@/lib/format';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * The admin overview: the tab that answers "is anything wrong".
 *
 * What this replaces was not a dashboard. It was two links in the sidebar --
 * Fleet and People -- and the Fleet page ended with two rows of prose containing
 * sentences like "DYSPATCH_HELIX_MODE is off, so no stories are read. Set it to
 * dry-run with DYSPATCH_HELIX_TOKEN, or to file with DYSPATCH_HELIX_MAP,
 * DYSPATCH_HELIX_LANES and DYSPATCH_HELIX_GH_TOKEN as well." Printing environment
 * variable names into the interface is the hub asking a person to be its
 * configuration file.
 *
 * The pass after that made it a page of figures, which was the opposite failure:
 * four under "Capacity", three under "Access", and every one of them a number
 * with no verb attached. Those figures are in the section's gradient band now
 * (`AdminShell`), where they are read once at the top of whichever admin tab you
 * are on, and this tab is what is left when the arithmetic moves out -- the two
 * questions that have answers a person acts on. Is anything misconfigured, and
 * is work arriving the way it should.
 *
 * Every figure is read from the same endpoints the rest of the console uses, so
 * nothing here can quote a number the dashboard disagrees with.
 *
 * The environment variable names did not become secret; they moved to the About
 * page, which is a reference, next to the rest of the operator documentation.
 */

/**
 * One intake adapter, described by what it does rather than by what turns it on.
 *
 * The hub's own `reason` string is the authority on why an adapter is off, and it
 * names settings. It stays, but as the small print under a sentence a person can
 * act on, rather than as the whole row.
 */
function AdapterRow({
  name,
  anchor,
  health,
  does,
}: {
  name: string;
  /** The `id` of this adapter's card in About's "Filing work" panel. */
  anchor: string;
  health: IntakeAdapterHealth | undefined;
  does: string;
}) {
  const mode = health?.mode ?? '…';
  return (
    <li className="px-gutter py-3.5">
      <div className="flex min-w-0 flex-wrap items-center gap-2.5">
        <span className="text-base font-medium">{name}</span>
        <Badge variant={mode === 'off' ? 'secondary' : mode === 'dry-run' ? 'warning' : 'success'}>
          {mode === 'off' ? 'not filing' : mode === 'dry-run' ? 'dry run' : 'filing'}
        </Badge>
        <Link
          to="/about"
          hash={anchor}
          className="text-fg-3 hover:text-foreground ml-auto shrink-0 text-xs transition-colors duration-100"
        >
          How to turn it on →
        </Link>
      </div>
      <p className="text-fg-2 mt-1 text-sm">{does}</p>
      {/* The hub's own sentence, folded away.
          It is the authority on why an adapter is off and it is worth keeping
          verbatim, but it is a list of environment variable names, and four of
          those across two rows was most of what made this screen read as a
          terminal in a web page. A native `details` keeps it one click away with
          no script and no state. */}
      {health?.reason ? (
        <details className="group mt-1.5">
          <summary className="text-fg-3 hover:text-foreground marker:content-[''] w-fit cursor-pointer list-none text-xs transition-colors duration-100">
            <span className="group-open:hidden">Why it is off</span>
            <span className="hidden group-open:inline">Hide the settings</span>
          </summary>
          <p className="text-fg-3 dy-inset mt-1.5 px-3 py-2 font-mono text-2xs leading-relaxed">
            {health.reason}
          </p>
        </details>
      ) : null}
    </li>
  );
}

/**
 * Run one dispatch sweep by hand.
 *
 * The toast says what the sweep actually did, including when the answer is
 * nothing, because "a queue that will not move" and "a scheduler that is not
 * running" look identical from the outside and this is the button that tells them
 * apart. It dispatches; it does not file. Nothing new enters the hub from it.
 */
function RunSweepButton() {
  const sweep = useHubAction(async () => {
    const { report } = await api.sweep();
    toast.success(sweepSentence(report));
  });
  return (
    <Button
      size="pill"
      variant="hero"
      onClick={() => sweep.run()}
      disabled={sweep.busy}
      title="Runs one dispatch sweep now: offers queued work to free devices, expires offers nobody acknowledged, reaps runs that went over. It does not run the intake adapters."
    >
      <Play aria-hidden />
      {sweep.busy ? 'Sweeping…' : 'Run scheduler now'}
    </Button>
  );
}

/** The people who joined most recently, each with the way in to their access. */
function AccessPreview() {
  const users = useUsers();
  const people = [...(users.data ?? [])]
    .sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''))
    .slice(0, 5);

  if (!people.length) return null;

  return (
    <Section>
      <SectionHead
        title="Access"
        meta={`${users.data?.length ?? 0} on this hub`}
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/people">All people and invites →</Link>
          </Button>
        }
      />
      <Surface>
        <ul className="divide-line divide-y">
          {people.map((person) => (
            <li key={person.id} className="flex min-w-0 items-center gap-3 px-gutter py-2.5">
              <span
                className="text-2xs grid size-7 shrink-0 place-items-center rounded-full font-semibold text-white"
                style={{ background: 'var(--dy-grad)' }}
                aria-hidden
              >
                {(person.name || person.email || '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium">
                  {person.name || person.email}
                </span>
                {person.name ? (
                  <span className="text-fg-3 block truncate text-xs">{person.email}</span>
                ) : null}
              </span>
              <Badge variant={person.isAdmin ? 'default' : 'secondary'}>
                {person.isAdmin ? 'admin' : 'member'}
              </Badge>
              <span className="text-fg-3 tnum hidden w-24 shrink-0 text-right text-xs sm:block">
                {person.devices.length} device{person.devices.length === 1 ? '' : 's'}
              </span>
              {/* Straight to their row on the People tab: the palette's own
                  `?focus=` contract, reused so this button lands on the person
                  rather than on a table of everybody. */}
              <Button size="sm" variant="outline" asChild className="shrink-0">
                <Link to="/admin/people" search={{ focus: person.id }}>
                  <SlidersHorizontal aria-hidden />
                  <span className="max-sm:sr-only">Manage access</span>
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      </Surface>
    </Section>
  );
}

/**
 * The fleet's identity policy and its tool catalog, read-only.
 *
 * Both are set by environment variable and neither was legible anywhere. An
 * admin could not answer "which Claude account does this fleet require" from
 * inside the console, which mattered because the worker's own refusal names
 * the policy and tells an engineer to go and ask: the remedy pointed at a
 * person who could not see the thing being asked about. The tool catalog was
 * worse, because a repo declaring a server the catalog does not have produces
 * a declined offer whose fix is the hub operator's.
 *
 * Read-only on purpose, and the row says which variable sets it. Editing
 * either from here would mean a hub whose configuration lives in two places,
 * and the deploy is still the place where a secret gets set. Seeing them stops
 * being a grep; setting them stays a deploy.
 */
function FleetConfiguration() {
  const briefing = useQuery<OnboardingBriefing>({
    queryKey: hubKeys.onboarding,
    queryFn: api.onboarding,
    staleTime: 5 * 60_000,
  });

  const policy = briefing.data?.policy;
  const rows: { variable: string; label: string; value: string | null }[] = [
    {
      variable: 'HUB_REQUIRED_CLAUDE_ORG',
      label: "The Claude organization an agent's account must belong to",
      value: policy?.claudeOrg ?? null,
    },
    {
      variable: 'HUB_REQUIRED_ACCOUNT_DOMAIN',
      label: 'The email domain that account must be on',
      value: policy?.accountDomain ?? null,
    },
    {
      variable: 'HUB_REQUIRED_GITHUB_ORG',
      label: 'The GitHub login, or org membership, gh must resolve to',
      value: policy?.githubOrg ?? null,
    },
  ];
  const tools = briefing.data?.tools ?? [];
  const configured = rows.filter((row) => row.value !== null).length;

  return (
    <Section>
      <SectionHead
        title="Fleet configuration"
        meta={
          briefing.isPending
            ? undefined
            : `${configured} of 3 identity rules set, ${tools.length} tool${tools.length === 1 ? '' : 's'} in the catalog`
        }
      />
      <Surface>
        <ul className="divide-line divide-y">
          {rows.map((row) => (
            <li key={row.variable} className="flex min-w-0 items-center gap-3 px-gutter py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium">
                  {row.value ?? <span className="text-fg-3">not set</span>}
                </span>
                <span className="text-fg-3 block truncate text-xs">{row.label}</span>
              </span>
              <code className="text-fg-3 shrink-0 font-mono text-2xs max-sm:hidden">
                {row.variable}
              </code>
            </li>
          ))}
          {tools.length === 0 ? (
            <li className="px-gutter py-2.5">
              <span className="text-fg-3 block text-sm">
                No MCP servers in the catalog, so a repo that declares one in its{' '}
                <code className="font-mono text-2xs">.dyspatch.json</code> has its offers declined.
                Set <code className="font-mono text-2xs">HUB_MCP_SERVERS</code> to add them.
              </span>
            </li>
          ) : (
            tools.map((tool) => (
              <li key={tool.name} className="flex min-w-0 items-center gap-3 px-gutter py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-sm">{tool.name}</span>
                  <span className="text-fg-3 block truncate text-xs">
                    {tool.transport}
                    {tool.variables.length > 0
                      ? `, needs ${tool.variables.join(' and ')} on every worker`
                      : ', needs no credential'}
                  </span>
                </span>
                <code className="text-fg-3 shrink-0 font-mono text-2xs max-sm:hidden">
                  HUB_MCP_SERVERS
                </code>
              </li>
            ))
          )}
        </ul>
      </Surface>
    </Section>
  );
}

export function AdminPage() {
  const health = useHealth();
  const stats = useStats();

  const queued = stats.data?.queue.queued ?? 0;
  const oldestQueued = stats.data?.queue.oldestQueuedMinutes ?? 0;
  const warnings = health.data?.warnings ?? [];

  return (
    <AdminShell tab="overview" actions={<RunSweepButton />}>
      <Section>
        <SectionHead
          title="Hub health"
          meta={
            oldestQueued
              ? `oldest queued task waiting ${duration(oldestQueued)}`
              : queued
                ? undefined
                : 'nothing waiting for a device'
          }
          hue={warnings.length ? 'var(--dy-warn)' : undefined}
        />
        <Surface>
          {warnings.length ? (
            <ul className={cn('divide-line divide-y')}>
              {warnings.map((warning) => (
                <li key={warning} className="flex items-start gap-3 px-gutter py-3">
                  <AlertTriangle className="text-warn mt-0.5 size-4 shrink-0" aria-hidden />
                  <p className="text-fg-2 min-w-0 text-sm">{warning}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-3 px-gutter py-4">
              <CheckCircle2 className="text-ok size-4 shrink-0" aria-hidden />
              <p className="text-base">
                Nothing is misconfigured.{' '}
                <span className="text-fg-3">
                  Database {health.data?.database ?? '…'}, scheduler sweeping every{' '}
                  {Math.round((health.data?.sweepIntervalMs ?? 0) / 1000)}s.
                </span>
              </p>
            </div>
          )}
        </Surface>
      </Section>

      <Section>
        <SectionHead title="Work arriving on its own" meta="intake adapters" />
        <Surface>
          <ul className="divide-line divide-y">
            <AdapterRow
              name="Nightly"
              anchor="intake-schedule"
              health={health.data?.intake.nightly}
              does="Files a standing docs and maintenance pass over the mapped repos, once a night."
            />
            <AdapterRow
              name="Helix"
              anchor="intake-helix"
              health={health.data?.intake.helix}
              does="Turns Helix L3 stories that have acceptance criteria into issues on their mapped repo."
            />
          </ul>
        </Surface>
      </Section>

      <FleetConfiguration />

      <AccessPreview />
    </AdminShell>
  );
}
